
import { graft as defaultGraft, IPatch, Patch, Delete, ReplacePatch } from '../../utilities/Graft';
import { IFaceted } from '../../models/Facet';
import { createActionCreator, IActionCreator, IAction } from '../../store/actions/createActionCreator';

/**
 * @public
 */
export interface IItemMapState {
  readonly items: {
    readonly [itemKey: string]: IFaceted;
  };
}

const INITIAL_ITEM_MAP_STATE: IItemMapState = {
  items: {}
};

/**
 * Primitive store action to graft new item information into the cache.
 *
 * In general, higher-order actions and components should not dispatch this action directly.
 * Prefer a 'thunk' action such as `resolveItems`, `invalidateItems`, or `clearItems`.
 *
 * @public
 */
export const updateItems: IActionCreator<'updateItems', {
  items: {
    [itemKey: string]: IPatch<IFaceted> | Patch<IFaceted> | Delete<IFaceted>;
  };
}> = createActionCreator('updateItems');

/**
 * Primitize store action which prunes all items in the cache except for the given set of 'preserved' items.
 *
 * This action can be very disruptive. In general, use `pruneLastAcessedItems`, which carefully preserves
 * items based on demand state and recent access.
 *
 * @public
 */
export const pruneItems: IActionCreator<'pruneItems', {
  items: {
    [itemKey: string]: {};
  };
}> = createActionCreator('pruneItems');

/**
 * Reducer which applies patches to the item cache.
 *
 * UX components should subscribe to a store managed by this reducer
 * and use it to obtain data about specific items.
 *
 * A `CacheMonitor` will dispatch an `updateItems` action whenever a
 * data source produces new information.
 *
 * Apply a `updateItems` action to make this reducer produce a new state.
 *
 * @internal
 */
export function itemMapReducer(state: IItemMapState = INITIAL_ITEM_MAP_STATE, action?: IAction | undefined, options: {
  graft?: typeof defaultGraft;
} = {}): IItemMapState {
  const {
    graft = defaultGraft
  } = options;

  if (action) {
    if (updateItems.matches(action)) {
      const patch: IPatch<{
        [itemKey: string]: IFaceted;
      }> = {};

      const {
        items
      } = action.payload;

      for (const itemKey in items) {
        if (Object.prototype.hasOwnProperty.call(items, itemKey)) {
          // TODO add key alias routing here.
          patch[itemKey] = items[itemKey];
        }
      }

      return graft(state, {
        items: patch
      });
    } else if (pruneItems.matches(action)) {
      const {
        items
      } = action.payload;

      const patch: {
        [itemKey: string]: IFaceted;
      } = {};

      for (const itemKey in items) {
        if (Object.prototype.hasOwnProperty.call(items, itemKey)) {
          // Preserve the previous state of each specified item.
          patch[itemKey] = state.items[itemKey];
        }
      }

      return graft(state, {
        items: new ReplacePatch(patch)
      });
    }
  }

  return state;
}

/**
 * @internal
 */
export function getItem(state: IItemMapState, itemKey: string): IFaceted {
  return state.items[itemKey];
}
